import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";
import AlertService from "../services/alert/AlertService";
import { ApiUrl } from "../utils/modules";

const AlertContext = createContext();

const getSocketBaseUrl = () => {
  return ApiUrl.replace(/\/api\/v1\/?$/, "");
};

const getAlertTypeLabel = (type) => {
  switch (type) {
    case 1:
      return "Inactivité";
    case 2:
      return "Connexion";
    case 3:
      return "Mot clé interdit";
    case 4:
      return "Installation logiciel";
    case 5:
      return "Navigation interdite";
    default:
      return "Alerte";
  }
};

const buildNotificationBody = (alert) => {
  const userFullName = `${alert?.user?.nom || ""} ${alert?.user?.prenom || ""}`.trim();
  const companyName = alert?.user?.entreprise?.nom || "";

  if (alert?.type === 3) {
    return `${userFullName} - mot clé: ${alert?.details?.keyword || ""}${
      companyName ? ` - ${companyName}` : ""
    }`;
  }

  if (alert?.type === 5) {
    return `${userFullName} - site: ${alert?.details?.site || ""}${
      companyName ? ` - ${companyName}` : ""
    }`;
  }

  return `${userFullName}${companyName ? ` - ${companyName}` : ""}`;
};

export const AlertProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastAlert, setLastAlert] = useState(null);
  const shownNotificationsRef = useRef(new Set());

  const userData = JSON.parse(localStorage.getItem("user") || "null");

  const askNotificationPermission = async () => {
    try {
      if (!("Notification" in window)) {
        /* console.log("Ce navigateur ne supporte pas Notification API"); */
        return;
      }

      if (Notification.permission === "default") {
        const permission = await Notification.requestPermission();
        /* console.log("Permission notification =", permission); */
      }
    } catch (error) {
      console.error("Erreur permission notification :", error);
    }
  };

  const showBrowserNotification = (alert) => {
    try {
      if (!("Notification" in window)) return;
      if (Notification.permission !== "granted") return;
      if (!alert?._id) return;

      if (shownNotificationsRef.current.has(alert._id)) return;
      shownNotificationsRef.current.add(alert._id);

      const title = `🔔 ${getAlertTypeLabel(alert.type)}`;
      const body = buildNotificationBody(alert);

      const notification = new Notification(title, {
        body,
        icon: "/images/logo/logo_icon.png",
        tag: `alert-${alert._id}`,
      });

      notification.onclick = () => {
        window.focus();
        window.location.href = "/liste_alert";
        notification.close();
      };
    } catch (error) {
      console.error("Erreur affichage notification navigateur :", error);
    }
  };

  useEffect(() => {
    if (!userData?._id) return;

    askNotificationPermission();

    const socketBaseUrl = getSocketBaseUrl();
    /* console.log("socketBaseUrl =", socketBaseUrl);
    console.log("user connecté =", userData._id); */

    const socket = io(socketBaseUrl, {
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => {
      /* console.log("✅ Socket front connecté :", socket.id); */
      socket.emit("registerAlertViewer", userData._id);
    });

    socket.on("alert:new", (alert) => {
      /* console.log("📩 Nouvelle alerte reçue :", alert); */
      setLastAlert(alert);
      setUnreadCount((prev) => prev + 1);
      showBrowserNotification(alert);
    });

    socket.on("alert:unread-count", ({ count }) => {
      /* console.log("🔢 Nouveau unread count :", count); */
      setUnreadCount(count || 0);
    });

    AlertService.getUnreadCount(userData._id)
      .then((res) => {
        /* console.log("📊 unread count API =", res.data.count, res.data); */
        setUnreadCount(res.data.count || 0);
      })
      .catch((err) => {
        console.error("Erreur getUnreadCount :", err);
      });

    return () => {
      socket.disconnect();
    };
  }, [userData?._id]);

  const markAllAsRead = async () => {
    if (!userData?._id) return;

    try {
      await AlertService.markAllAsRead(userData._id);
      setUnreadCount(0);
    } catch (error) {
      console.error("Erreur markAllAsRead :", error);
    }
  };

  const value = useMemo(
    () => ({
      unreadCount,
      lastAlert,
      markAllAsRead,
      setUnreadCount,
      askNotificationPermission,
    }),
    [unreadCount, lastAlert]
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlertContext = () => useContext(AlertContext);